import qs from 'qs';
import {
  FAILURE_REASON_STATUSES,
  QUERY_PARAM,
  REFERENCE_NUMBER_SEPARATOR,
  SearchByOption,
} from './GlobalTrack.constants';
import { getConsignmentStatusPrettyName } from 'src/utils/consignmentStatus';
import { getInternalNdrPrettyName } from 'src/utils/domesticConsignment';
import {
  TrackingConsignment,
  TrackingEvent,
  TrackSearchPayload,
  TrackSearchState,
} from './GlobalTrack.types';

export const splitReferenceNumbers = (input: string): string[] => {
  if (!input) {
    return [];
  }
  return input.split(REFERENCE_NUMBER_SEPARATOR).map((token) => token.trim());
};

const isValidSearchBy = (value: unknown): value is SearchByOption =>
  value === SearchByOption.REFERENCE_NUMBER ||
  value === SearchByOption.COURIER_PARTNER_REFERENCE_NUMBER ||
  value === SearchByOption.CUSTOMER_REFERENCE_NUMBER;

export const generateTrackQuery = (payload: TrackSearchPayload): string =>
  qs.stringify(
    {
      [QUERY_PARAM.SEARCH_BY]: payload.searchBy,
      [QUERY_PARAM.REFERENCE_NUMBERS]: payload.numbers.join(REFERENCE_NUMBER_SEPARATOR),
      [QUERY_PARAM.CUSTOMER_CODE]: payload.code || undefined,
      [QUERY_PARAM.COURIER_PARTNER]: payload.courierPartner || undefined,
    },
    { skipNulls: true },
  );

export const parseTrackQuery = (search: string): TrackSearchState | null => {
  const parsed = qs.parse(search, { ignoreQueryPrefix: true });

  const referenceNumbersRaw = parsed[QUERY_PARAM.REFERENCE_NUMBERS];
  if (typeof referenceNumbersRaw !== 'string' || !referenceNumbersRaw.trim()) {
    return null;
  }

  const numbers = splitReferenceNumbers(referenceNumbersRaw);
  if (!numbers.length) {
    return null;
  }

  const searchByRaw = parsed[QUERY_PARAM.SEARCH_BY];
  const searchBy = isValidSearchBy(searchByRaw) ? searchByRaw : SearchByOption.REFERENCE_NUMBER;

  const codeRaw = parsed[QUERY_PARAM.CUSTOMER_CODE];
  const courierPartnerRaw = parsed[QUERY_PARAM.COURIER_PARTNER];

  return {
    searchBy,
    numbers,
    code: typeof codeRaw === 'string' ? codeRaw : undefined,
    courierPartner: typeof courierPartnerRaw === 'string' ? courierPartnerRaw : undefined,
  };
};

export const buildLocationText = (event: {
  hub_name?: string;
  hub_city?: string;
  hub_state?: string;
  location?: string;
}): string => {
  if (event.hub_name || event.hub_city || event.hub_state) {
    return [event.hub_name, event.hub_city || event.hub_state].filter(Boolean).join(' | ');
  }
  if (event.location && event.location !== '""') {
    return event.location;
  }
  return '';
};

export const prettifyStatus = (value?: string | null): string =>
  (value || '')
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export const resolveEventDescription = (
  event: Pick<TrackingEvent, 'status_internal' | 'event_description' | 'event_label' | 'pretty_name_overrides'>,
  t: (key: string) => string,
  language: string,
): string => {
  const override = language ? event.pretty_name_overrides?.[language] : undefined;
  if (override) return override;

  if (event.event_label) return t(event.event_label);

  if (event.status_internal) {
    return t(getConsignmentStatusPrettyName(event.status_internal));
  }

  return event.event_description || '';
};

export const isDeliveredStatus = (...values: Array<string | null | undefined>): boolean =>
  values.some((value) => value === 'delivered' || value === 'rto_delivered');


export const resolveHeadlineStatus = (
  item: Pick<TrackingConsignment, 'status' | 'displayStatus'>,
  language: string,
): string => {
  const ds = item.displayStatus;
  if (ds) {
    const override = language ? ds.pretty_name_overrides?.[language] : undefined;
    if (override) return override;
    if (ds.status) {
      const mapped = getConsignmentStatusPrettyName(ds.status);
      if (mapped !== ds.status) return mapped;
    }
    return ds.display_name || item.status || '';
  }
  return item.status || '';
};

export const resolveFailureReasonLabel = (
  event: Pick<TrackingEvent, 'failure_reason_id' | 'failure_reason' | 'status_internal'>,
): string | null => {
  if (!FAILURE_REASON_STATUSES.includes(event.status_internal as (typeof FAILURE_REASON_STATUSES)[number])) {
    return null;
  }
  if (event.failure_reason) {
    return event.failure_reason;
  }
  if (event.failure_reason_id) {
    const mapped = getInternalNdrPrettyName(event.failure_reason_id);
    return mapped === event.failure_reason_id ? null : mapped;
  }
  return null;
};
